"use client";

import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { IS_KLAP_FREE_FAQ } from "../const/publicPricing";
import { faqPageJsonLd } from "../const/structuredData";
import { useMessages } from "../context/i18n";
import { common as enCommon } from "../dictionaries/en/common";
import { reactText } from "../utils/reactText";
import { JsonLd } from "./JsonLd";
import Typography from "./Typography";

async function openLiveChat() {
  const intercom = await import("../lib/intercom");
  await intercom.show();
}

// Opens the chat from an FAQ answer.
export const LiveChatLink = ({ children }: { children: React.ReactNode }) => (
  <button
    type="button"
    className="text-violet-600 underline underline-offset-2 cursor-pointer bg-transparent p-0 m-0 font-inherit"
    onClick={openLiveChat}
  >
    {children}
  </button>
);

export type FAQItem = {
  question: string;
  answer: React.ReactNode;
  // The JSON-LD's answer when `answer` is JSX passed from a Server Component,
  // whose text can't be read here.
  answerText?: string;
};

interface FAQProps {
  className?: string;
  faq?: FAQItem[];
  /** Also render the questions as FAQPage JSON-LD. Leave off on pages that
   * already emit their own FAQPage. */
  jsonLd?: boolean;
}

// Exported so pages that need to APPEND items (e.g. the Opus Clip
// comparison page, which adds 6 extra Q&As without losing the originals)
// can merge: `[...DEFAULT_FAQ, ...extraFaq]` instead of re-duplicating
// the 5 default questions in their own data file.
export const DEFAULT_FAQ: FAQItem[] = [
  {
    question: "How does Klap work?",
    answer: (
      <>
        Once you upload a video, Klap uses artificial intelligence (AI) to
        identify the most interesting topics. Using intelligent framing and
        facial recognition to create responsive subtitles, it then converts
        these topics into shorter clips. In just one click, you can convert a
        3-minute YouTube video into 5 ready-to-publish videos for Instagram or
        TikTok. It’s that simple.
      </>
    ),
  },
  {
    question: "What kind of videos are supported?",
    answer: (
      <>
        Klap works on any content where there’s speech: podcasts,
        interviews, webinars, gaming and sports commentary, tutorials, product
        reviews, trainings, and more.
      </>
    ),
  },
  {
    question: "What languages are supported?",
    answer: (
      <>
        Klap edits and transcribes videos in 99+ languages including: English,
        French, German, Spanish, Italian, Portuguese, Dutch, Danish, Swedish,
        Norwegian, Finnish, Icelandic, Estonian, Latvian, Lithuanian, Afrikaans,
        Arabic, Armenian, Azerbaijani, Belarusian, Bosnian, Bulgarian, Catalan,
        Chinese, Croatian, Czech, Galician, Greek, Hebrew, Hindi, Hungarian,
        Indonesian, Japanese, Kannada, Kazakh, Korean, Macedonian, Malay,
        Marathi, Maori, Nepali, Persian, Polish, Romanian, Russian, Serbian,
        Slovak, Slovenian, Swahili, Tagalog, Tamil, Thai, Turkish, Ukrainian,
        Urdu, Vietnamese, and Welsh.
      </>
    ),
  },
  {
    question: IS_KLAP_FREE_FAQ.question,
    answer: IS_KLAP_FREE_FAQ.answerText,
  },
  {
    question: "I have more questions. How can I contact you?",
    answer: (
      <>
        Feel free to contact us on{" "}
        <a
          className="text-violet-600 underline underline-offset-2"
          href="https://discord.gg/KcAcHdrSQU"
        >
          Discord
        </a>{" "}
        or by{" "}
        <LiveChatLink>live chat.</LiveChatLink>
      </>
    ),
  },
  // {
  //   question: "How long does it take to process a video?",
  //   answer: (
  //     <>
  //       It depends on the length of the video but it usually takes less than 15
  //       minutes.
  //     </>
  //   ),
  // },
  // {
  //   question: "How long can my videos be?",
  //   answer: (
  //     <>
  //       You can upload videos up to 2 hours long using Klap Pro and 30 minutes
  //       long using the free plan. Need longer videos? Contact us at
  //       team@klap.app
  //     </>
  //   ),
  // },
];

const FAQ = (props: FAQProps) => {
  const items = props.faq || DEFAULT_FAQ;
  const m = useMessages("common", enCommon);
  return (
    <section className={`${props.className}`}>
      {props.jsonLd && (
        <JsonLd
          data={[
            faqPageJsonLd(
              items.map((faq) => ({
                question: faq.question,
                answer:
                  faq.answerText ??
                  reactText(faq.answer).replace(/\s+/g, " ").trim(),
              })),
            ),
          ]}
        />
      )}
      <Typography className="text-center" variant="h2">
        {m.faqTitle}
      </Typography>
      <div className="mt-16 font-display w-full flex flex-col text-left items-stretch justify-start gap-8">
        {items.map((faq) => {
          return (
            <details
              key={faq.question}
              className=" font-light list-none group open:bg-slate-100 rounded-2xl"
            >
              <summary className="text-xl font-normal list-none flex items-center justify-stretch hover:bg-slate-100 p-4 rounded-2xl cursor-pointer">
                <Typography className="text-slate-900" variant="subtitle1">
                  {faq.question}
                </Typography>
                <ChevronDownIcon className="w-4 h-4 ml-auto group-open:rotate-180 shrink-0 text-slate-500" />
              </summary>
              <div className="px-4 pb-4">
                <Typography variant="body2" className="text-slate-600">
                  {faq.answer}
                </Typography>
              </div>
            </details>
          );
        })}
      </div>
    </section>
  );
};

export default FAQ;
